/**
 * Notifications — the Message Center page (`/notifications`), on mockup
 * carbon/01's brief and its "Changes since last week" structured list.
 *
 * The bell dropdown in the header is the quick surface; this page is the
 * full feed for triage:
 *
 *   1. The brief (BriefHero): an action-first h1 from the feed's counts, one
 *      lede naming how many items are open, and a provenance line. The
 *      breadcrumb carries the page name.
 *   2. The feed tile: its heading and description, then two content
 *      switchers (category, and overdue / due soon), purely client-side
 *      over the loaded feed (no extra API params).
 *   3. Sections in priority order (Overdue, Due soon, Upcoming), each a
 *      structured list of NotificationItem rows (severity by shape and word,
 *      a "New" tag while unread), then Recently dismissed, collapsed.
 *
 * Data wiring mirrors the bell (NotificationsBell.tsx) so the cache stays
 * coherent across both surfaces:
 *   - Same react-query key: `['notifications', tenantId]`.
 *   - Same optimistic dismissal shape (snapshot → remove item → rollback on
 *     error → invalidate on settle).
 *
 * Voice (CLAUDE.md Product Voice, DESIGN.md): the headline leads with the
 * action; the all-clear is "Nothing to review."; the "heads-up" bucket reads
 * "Upcoming" (terminology decision #3). Nothing here carries a hue except
 * the critical octagon on critical rows.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { ActionBrief } from '@/lib/brief-shape'
import { useTenantContext } from '@/hooks/useTenantContext'
import { formatRelativeTime } from '@/lib/brief-helpers'
import {
  groupForNotification,
  partitionByGroup,
  spellSmallNumber,
} from '@/lib/notification-deck'
import type {
  NotificationCategory,
  NotificationDto,
  NotificationFeed,
  NotificationGroup,
} from '@/types/notifications'
import {
  BriefHero,
  ContentSwitcher,
  IconChevronRight,
  InlineNotification,
  Skeleton,
  Tile,
} from '@/components/carbon'
import NotificationItem from '@/components/Notifications/NotificationItem'

// -----------------------------------------------------------------------------
// Filter model
// -----------------------------------------------------------------------------
//
// Two independent single-select switchers. Category filters by source bucket;
// severity filters by the derived (severity, daysUntil) group. Both default to
// `all` and combine with AND semantics.

type CategoryFilter = 'all' | NotificationCategory
type SeverityFilter = 'all' | 'overdue' | 'due-soon'

interface FilterOption<T extends string> {
  value: T
  /** The switcher segment's label, sentence case. */
  label: string
}

const CATEGORY_OPTIONS: FilterOption<CategoryFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'waiver', label: 'Waivers' },
  { value: 'app-credential', label: 'Credentials' },
  { value: 'app-review', label: 'Reviews' },
]

const SEVERITY_OPTIONS: FilterOption<SeverityFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'due-soon', label: 'Due soon' },
]

// -----------------------------------------------------------------------------
// The brief
// -----------------------------------------------------------------------------
//
// Action-first (DESIGN.md "The Action-First Headline Rule"): the headline
// names what to do first, the lede names how many items are open in one
// sentence with one bold run. Counts are over the active (non-dismissed)
// feed, unfiltered, so the brief never changes with the switchers.

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

function composeNotificationsBrief(active: NotificationDto[]): ActionBrief {
  const total = active.length
  if (total === 0) {
    return {
      headline: 'Nothing to review.',
      lede: '**Nothing is open** across waivers, app credentials and reviews.',
    }
  }
  const counts = { overdue: 0, 'due-soon': 0, 'heads-up': 0 } as Record<NotificationGroup, number>
  for (const item of active) counts[groupForNotification(item)]++
  const overdue = counts.overdue
  const dueSoon = counts['due-soon']
  const items = `${total} ${plural(total, 'item', 'items')}`

  if (overdue > 0) {
    return {
      headline: overdue === 1 ? 'Close the overdue item first.' : `Close the ${overdue} overdue items first.`,
      lede:
        overdue === total
          ? `**${items} ${plural(total, 'is', 'are')} overdue**.`
          : `**${items} need attention**, and ${overdue} of them ${plural(overdue, 'is', 'are')} overdue.`,
    }
  }
  if (dueSoon > 0) {
    return {
      headline:
        dueSoon === 1 ? 'Review the item due within two weeks.' : `Review the ${dueSoon} items due within two weeks.`,
      lede:
        dueSoon === total
          ? `**${items} ${plural(total, 'is', 'are')} due within two weeks**.`
          : `**${items} need attention**, and ${dueSoon} of them ${plural(dueSoon, 'is', 'are')} due within two weeks.`,
    }
  }
  return {
    headline: total === 1 ? 'Plan for the upcoming item.' : `Plan for the ${total} upcoming items.`,
    lede: total === 1 ? `**${items} is open**, and it is not overdue.` : `**${items} are open**, and none is overdue.`,
  }
}

/** No tenant in scope (a global admin's all-tenants view): the feed is per tenant. */
const NO_TENANT_BRIEF: ActionBrief = {
  headline: 'Choose a tenant to see its notifications.',
  lede: '**Notifications are kept per tenant**, so pick one in the tenant switcher at the top of the side nav.',
}

/** The feed query failed before any data arrived. */
const ERROR_BRIEF: ActionBrief = {
  headline: 'Reload the page to see the feed.',
  lede: '**The notifications feed did not load**, so Guardian has nothing to list yet.',
}

// -----------------------------------------------------------------------------
// Section copy
// -----------------------------------------------------------------------------
//
// Each section gets a short helper line under its heading. Numerals 0-9 spell
// out (via `spellSmallNumber`); singular vs plural picks the right voice.

/** One short line. Verb tense matches the section's posture (now / soon / later). */
function composeSectionDeck(group: NotificationGroup, count: number): string {
  if (count === 0) return ''
  switch (group) {
    case 'overdue':
      return count === 1
        ? 'One item needs attention now.'
        : `${spellSmallNumber(count)} items need attention now.`
    case 'due-soon':
      return count === 1
        ? 'One item is close to lapsing.'
        : `${spellSmallNumber(count)} items are close to lapsing.`
    case 'heads-up':
      return count === 1
        ? 'One item comes due later.'
        : `${spellSmallNumber(count)} items come due later.`
  }
}

/** Section headings; the `heads-up` key stays as the identifier, its label is "Upcoming" (terminology decision #3). */
const SECTION_LABEL: Record<NotificationGroup, string> = {
  overdue: 'Overdue',
  'due-soon': 'Due soon',
  'heads-up': 'Upcoming',
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export default function Notifications(): React.ReactElement {
  const queryClient = useQueryClient()
  const { defaultTenantId } = useTenantContext()

  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all')

  // Match the bell's cache key so dismiss/mark-read keep both surfaces
  // consistent. `'none'` placeholder mirrors NotificationsBell when global-
  // admin "All Tenants" mode leaves defaultTenantId undefined — keeps the
  // tuple shape stable so the cache never branches on undefined.
  const queryKey = ['notifications', defaultTenantId ?? 'none'] as const
  const isQueryEnabled = Boolean(defaultTenantId)

  const { data: feed, isError } = useQuery<NotificationFeed>({
    queryKey,
    queryFn: () => api.getNotifications(defaultTenantId as string),
    enabled: isQueryEnabled,
    // Page-level surface refetches on focus so a user returning from a tab
    // (e.g. after rotating a credential) sees the feed reconcile without a
    // manual reload. Bell is poll-driven; the page is focus-driven.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  // Optimistic single dismissal. Identical shape to NotificationsBell so the
  // shared cache key produces the same UI updates on both surfaces. After
  // success the item is flagged `isDismissed: true` rather than removed —
  // that's what lets the "Recently dismissed" section render it without a
  // second API call.
  const dismissMutation = useMutation({
    mutationFn: ({ tenantId, key }: { tenantId: string; key: string }) =>
      api.dismissNotification(tenantId, key),
    onMutate: async ({ key }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<NotificationFeed>(queryKey)
      if (previous) {
        const item = previous.items.find(i => i.key === key)
        const wasUnread = item && !item.isRead
        // Flip the local flag instead of removing the row outright. The bell
        // sees this via partitionByGroup (which already skips isDismissed),
        // and the page's "Recently dismissed" section picks it up.
        const items = previous.items.map(i =>
          i.key === key ? { ...i, isDismissed: true, isRead: true } : i,
        )
        queryClient.setQueryData<NotificationFeed>(queryKey, {
          ...previous,
          items,
          unreadCount: Math.max(0, previous.unreadCount - (wasUnread ? 1 : 0)),
        })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      // Rollback to the pre-mutation snapshot. `context` is undefined only if
      // onMutate threw before returning — guarded defensively.
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      // Reconcile with the server. Covers both success (server may have
      // noticed the source row resolved before we did) and error.
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const handleDismiss = (key: string) => {
    if (!defaultTenantId) return
    dismissMutation.mutate({ tenantId: defaultTenantId, key })
  }

  // -- Filter pipeline ------------------------------------------------------
  //
  // Order: drop dismissed → category filter → severity filter (via group).
  // Severity 'all' lets `heads-up` through; 'overdue' / 'due-soon' restrict
  // to that single group so the page collapses to one section when the user
  // narrows in.
  const activeItems = useMemo(() => feed?.items.filter(i => !i.isDismissed) ?? [], [feed])
  const dismissedItems = useMemo(() => feed?.items.filter(i => i.isDismissed) ?? [], [feed])

  const filteredActive = useMemo(() => {
    return activeItems.filter(item => {
      if (categoryFilter !== 'all' && item.category !== categoryFilter) return false
      if (severityFilter !== 'all') {
        const group = groupForNotification(item)
        if (group !== severityFilter) return false
      }
      return true
    })
  }, [activeItems, categoryFilter, severityFilter])

  const groups = useMemo(() => partitionByGroup(filteredActive), [filteredActive])

  // Upcoming only shows when the severity filter is `all`. When the user
  // explicitly narrows to overdue or due-soon, the upcoming section is out of
  // scope and would otherwise read as noise.
  const sectionsToRender: NotificationGroup[] = useMemo(() => {
    const order: NotificationGroup[] = ['overdue', 'due-soon']
    if (severityFilter === 'all') order.push('heads-up')
    return order
  }, [severityFilter])

  // -- Brief ----------------------------------------------------------------

  const brief = useMemo(() => {
    if (!isQueryEnabled) return NO_TENANT_BRIEF
    if (!feed && isError) return ERROR_BRIEF
    return composeNotificationsBrief(activeItems)
  }, [isQueryEnabled, feed, isError, activeItems])
  const briefLoading = isQueryEnabled && !feed && !isError

  // The provenance line, once the feed has arrived: formatRelativeTime
  // returns "Updated 5 minutes ago" (or "Updated 2 October" past a week), and
  // the line names Guardian as the one who updated it, then the feed's scope.
  const refreshedTail = formatRelativeTime(feed?.lastComputedAt)?.replace(/^Updated\s+/, '')
  const refreshedWhen = !refreshedTail
    ? ''
    : /(ago|just now)$/.test(refreshedTail)
      ? ` ${refreshedTail}`
      : ` on ${refreshedTail}`
  const provenance = feed ? `Updated by Guardian${refreshedWhen} · This tenant` : undefined

  // -- Render ---------------------------------------------------------------

  const totalAfterFilter = filteredActive.length
  const hasAnyActive = activeItems.length > 0
  // The feed tile needs a tenant, and a feed or a load in flight; a failed first load leaves only the brief.
  const showFeed = isQueryEnabled && !(isError && !feed)

  return (
    <div>
      <section aria-labelledby="notifications-verdict" className="mt-8">
        <BriefHero
          brief={brief}
          size="pillar"
          headingId="notifications-verdict"
          isLoading={briefLoading}
          provenance={provenance}
          className="[overflow-wrap:anywhere]"
        />
      </section>

      {showFeed && (
        <>
          <Tile as="section" padding={0} aria-labelledby="notifications-feed-heading" className="mt-12 min-w-0">
            <div className="px-4 pb-6 pt-4 c-md:px-5">
              <h2 id="notifications-feed-heading" className="m-0 text-heading font-normal text-text-primary">
                What needs attention
              </h2>
              <p className="m-0 mt-0.5 text-body text-text-secondary">
                Waivers, app credentials and review dates in one feed. Dismissed items return if the
                underlying state changes.
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-4 px-4 pb-4 c-md:flex-row c-md:flex-wrap c-md:gap-8 c-md:px-5">
              <FilterSwitcher
                label="Category"
                options={CATEGORY_OPTIONS}
                value={categoryFilter}
                onChange={setCategoryFilter}
              />
              <FilterSwitcher
                label="Severity"
                options={SEVERITY_OPTIONS}
                value={severityFilter}
                onChange={setSeverityFilter}
              />
            </div>

            {isError && feed && (
              <div className="px-4 pb-4 c-md:px-5">
                <InlineNotification kind="error" title="The feed did not refresh.">
                  The items below are from the last update.
                </InlineNotification>
              </div>
            )}

            {!feed && (
              <div className="border-t border-line-subtle px-4 py-6 c-md:px-5">
                <Skeleton lines={3} label="Loading notifications" />
              </div>
            )}

            {feed && !hasAnyActive && (
              <p className="m-0 border-t border-line-subtle px-4 py-6 text-helper text-text-helper c-md:px-5">
                Items return here when a waiver, an app credential or a review nears its date.
              </p>
            )}

            {hasAnyActive && totalAfterFilter === 0 && (
              <p className="m-0 border-t border-line-subtle px-4 py-6 text-helper text-text-helper c-md:px-5">
                Nothing to review in this filter. Widen it to see the other items.
              </p>
            )}

            {hasAnyActive &&
              totalAfterFilter > 0 &&
              sectionsToRender.map(group => {
                const items = groups[group]
                if (items.length === 0) return null
                return <Section key={group} group={group} items={items} onDismiss={handleDismiss} />
              })}
          </Tile>

          {/* Recently dismissed, collapsed by default. Native <details> keeps
              the disclosure keyboard-first. */}
          <DismissedSection items={dismissedItems} onDismiss={handleDismiss} />
        </>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

interface FilterSwitcherProps<T extends string> {
  label: string
  options: FilterOption<T>[]
  value: T
  onChange: (next: T) => void
}

/**
 * One single-select filter: a helper label over a Carbon content switcher
 * (mockup 01's table filter). It picks one value out of a set, so it is a
 * radio group (radiogroup / radio / aria-checked, arrow keys move and check)
 * with the same accessible name as the visible label.
 */
function FilterSwitcher<T extends string>({ label, options, value, onChange }: FilterSwitcherProps<T>) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span aria-hidden="true" className="text-helper text-text-helper">
        {label}
      </span>
      <ContentSwitcher
        semantics="radio"
        aria-label={label}
        items={options.map(o => ({ id: o.value, label: o.label }))}
        value={value}
        onChange={id => onChange(id as T)}
        className="min-w-0 max-w-full"
      />
    </div>
  )
}

interface SectionProps {
  group: NotificationGroup
  items: NotificationDto[]
  onDismiss: (key: string) => void
}

/**
 * One section of the feed: a heading with its helper line, then a
 * structured list of comfortable `NotificationItem` rows.
 */
function Section({ group, items, onDismiss }: SectionProps) {
  const deck = composeSectionDeck(group, items.length)
  return (
    <section aria-labelledby={`notif-section-${group}`} className="border-t border-line-subtle">
      <div className="px-4 pb-2 pt-4 c-md:px-5">
        <h3 id={`notif-section-${group}`} className="m-0 text-body font-semibold leading-[1.29] text-text-primary">
          {SECTION_LABEL[group]}
        </h3>
        <p className="m-0 mt-0.5 text-helper text-text-helper">{deck}</p>
      </div>

      <div className="border-t border-line-subtle">
        {items.map(item => (
          <NotificationItem key={item.key} item={item} onDismiss={onDismiss} size="comfortable" />
        ))}
      </div>
    </section>
  )
}

interface DismissedSectionProps {
  items: NotificationDto[]
  onDismiss: (key: string) => void
}

/**
 * Collapsed disclosure for dismissed items. When the feed has no dismissed
 * rows, it still says where dismissed items will live.
 *
 * Dismissed rows reuse `NotificationItem` so the copy stays consistent. The
 * dismiss button stays wired to the same handler — the server upsert is
 * idempotent for already-dismissed keys, and the shared cache invalidation
 * reconciles any drift.
 */
function DismissedSection({ items, onDismiss }: DismissedSectionProps) {
  const hasAny = items.length > 0
  return (
    <details className="group mt-8 bg-layer-1">
      <summary
        className="
          flex min-h-12 cursor-pointer list-none items-center gap-3 px-4 c-md:px-5
          text-body text-text-primary
          transition-colors duration-110 ease-carbon hover:bg-layer-hover
          [&::-webkit-details-marker]:hidden
        "
      >
        <IconChevronRight className="transition-transform duration-150 ease-carbon group-open:rotate-90" />
        <span className="font-semibold">Recently dismissed</span>
        <span className="ml-auto text-helper text-text-helper">
          {hasAny ? `${items.length} hidden` : 'Empty for now'}
        </span>
      </summary>

      <div className="border-t border-line-subtle">
        {hasAny ? (
          items.map(item => <NotificationItem key={item.key} item={item} onDismiss={onDismiss} size="comfortable" />)
        ) : (
          <p className="m-0 px-4 py-6 text-helper text-text-helper c-md:px-5">
            Recently dismissed items will appear here.
          </p>
        )}
      </div>
    </details>
  )
}
